<template>
  <div>
    <!-- Logo section - only visible on desktop -->
    <div class="main-header-left d-none d-lg-block">
      <div class="logo-wrapper my-3">
        <router-link
          to="/"
          aria-label="Home"
        >
          <img
            src="../assets/images/white_logo.png"
            style="height: 50px"
            alt="Logo"
            class="logo-image"
          >
        </router-link>
      </div>
    </div>

    <!-- Main sidebar container with custom scrollbar -->
    <div
      class="sidebar custom-scrollbar pt-5"
      role="navigation"
      aria-label="Main Navigation"
    >
      <!-- User profile section -->
      <div
        v-if="user"
        class="sidebar-user text-center"
      >
        <div class="user-avatar-container">
          <img
            class="img-60 rounded-circle bg-white shadow user-avatar"
            :src="getImgUrl()"
            :alt="sessionType === 'personal' ? `${user.name} ${user.surname}` : company.name"
          >
          <div class="verification-badge">
            <img
              v-if="company?.systemVerifiedCompany===2"
              src="../assets/images/verified.svg"
              alt="Verified Icon"
              class="verification-icon verified"
            >
            <img
              v-else
              src="../assets/images/unverified.svg"
              alt="Unverified Icon"
              class="verification-icon unverified"
            >
          </div>
        </div>

        <!-- Personal account info -->
        <div
          v-if="sessionType ==='personal'"
          class="user-info"
        >
          <h6 class="mt-3 f-14 user-name">
            {{ user.name }} {{ user.surname }}
          </h6>
          <p class="user-id">
            {{ user.uniqueId }}
          </p>
        </div>

        <!-- Company account info -->
        <div
          v-else
          class="company-info"
        >
          <h6 class="mt-3 mb-2 f-14 company-name">
            {{ company.name }} {{ getCompanyFormat(company.format) }}
          </h6>

          <ProfileTypeBadge
            :profile="profile"
            :badge-white="true"
          />
        </div>
      </div>

      <!-- Sidebar menu -->
      <ul
        id="myDIV"
        class="sidebar-menu"
        :style="[layout.settings.sidebar.type === 'horizontal_sidebar' ? layout.settings.layout_type==='rtl'? {'margin-right': margin+'px'} : {'margin-left': margin+'px'} : { margin : '0px'}]"
        role="menu"
      >
        <!-- Left scroll arrow -->
        <li
          class="left-arrow"
          :class="{'d-none': layout.settings.layout_type==='rtl'? hideLeftArrowRTL: hideLeftArrow}"
          role="button"
          tabindex="0"
          aria-label="Scroll left"
          @click="(layout.settings.sidebar.type === 'horizontal_sidebar' && layout.settings.layout_type==='rtl') ? scrollToLeftRTL() : scrollToLeft()"
        >
          <i
            class="fa fa-angle-left"
            aria-hidden="true"
          />
        </li>

        <!-- Main dashboard section -->
        <li
          class="menu-group-header"
          role="presentation"
        >
          {{ $t('sidebar.sasShif') }}
        </li>
        <li
          class="menu-item"
          role="menuitem"
        >
          <router-link
            :to="{name:'welcome'}"
            class="menu-link"
          >
            <feather
              type="home"
              class="menu-icon"
              aria-hidden="true"
            />
            <span>{{ $t('sidebar.lvh') }}</span>
          </router-link>
        </li>

        <!-- Company details section -->
        <template v-if="sessionType==='company'">
          <li
            class="menu-group-header mt-4"
            role="presentation"
          >
            {{ $t('sidebar.irktDetallar') }}
          </li>
          <li
            v-if="profile !== 'PROCUREMENT'"
            class="menu-item"
            role="menuitem"
          >
            <router-link
              :to="{name:'offers'}"
              class="menu-link"
            >
              <feather
                type="tag"
                class="menu-icon"
                aria-hidden="true"
              />
              <span>Təkliflərim</span>
            </router-link>
          </li>
          <li
            class="menu-item"
            role="menuitem"
          >
            <router-link
              :to="{name:'companyProfile'}"
              class="menu-link"
            >
              <feather
                type="briefcase"
                class="menu-icon"
                aria-hidden="true"
              />
              <span>{{ $t('sidebar.irktProfili') }}</span>
            </router-link>
          </li>
          <li
            class="menu-item"
            role="menuitem"
          >
            <router-link
              :to="{name:'subscriptionPage'}"
              class="menu-link"
            >
              <feather
                type="bookmark"
                class="menu-icon"
                aria-hidden="true"
              />
              <span>{{ $t('sidebar.abunliyim') }}</span>
            </router-link>
          </li>
          <li
            class="menu-item"
            role="menuitem"
          >
            <router-link
              :to="{name:'invoicesPage'}"
              class="menu-link"
            >
              <feather
                type="credit-card"
                class="menu-icon"
                aria-hidden="true"
              />
              <span>{{ $t('sidebar.dmlrim') }}</span>
            </router-link>
          </li>
          <li
            v-if="company.legalForm !== 'PERSONAL'"
            class="menu-item"
            role="menuitem"
          >
            <router-link
              :to="{name:'companyBoard'}"
              class="menu-link"
            >
              <feather
                type="layout"
                class="menu-icon"
                aria-hidden="true"
              />
              <span>{{ $t('dashboardCompany.irktLvhsi') }}</span>
            </router-link>
          </li>
        </template>

        <!-- Help section -->
        <li
          class="menu-group-header mt-4"
          role="presentation"
        >
          {{ $t('sidebar.yardm') }}
        </li>
        <li
          class="menu-item"
          role="menuitem"
        >
          <router-link
            :to="{name:'faq'}"
            class="menu-link"
          >
            <feather
              type="help-circle"
              class="menu-icon"
              aria-hidden="true"
            />
            <span>{{ $t('sidebar.teztezVerilnSuallar') }}</span>
          </router-link>
        </li>
        <li
          class="menu-item"
          role="menuitem"
        >
          <router-link
            :to="{name:'page', params:{slug: 'terms'}}"
            class="menu-link"
          >
            <feather
              type="file-text"
              class="menu-icon"
              aria-hidden="true"
            />
            <span>{{ $t('sidebar.rtlr') }}</span>
          </router-link>
        </li>
        <li
          class="menu-item"
          role="menuitem"
        >
          <router-link
            :to="{name:'page', params:{slug: 'privacy'}}"
            class="menu-link"
          >
            <feather
              type="shield"
              class="menu-icon"
              aria-hidden="true"
            />
            <span>{{ $t('sidebar.mxfilikSiyasti') }}</span>
          </router-link>
        </li>
        <li
          class="menu-item"
          role="menuitem"
        >
          <router-link
            :to="{name:'feedback'}"
            class="menu-link"
          >
            <feather
              type="message-circle"
              class="menu-icon"
              aria-hidden="true"
            />
            <span>{{ $t('sidebar.dstk') }}</span>
          </router-link>
        </li>

        <!-- Account change section -->
        <li
          class="menu-item mt-5 mb-2"
          role="menuitem"
        >
          <router-link
            :to="{name:'changeAccount'}"
            class="menu-link"
          >
            <feather
              type="refresh-cw"
              class="menu-icon"
              aria-hidden="true"
            />
            <span>{{ $t('sidebar.hesabDyi') }}</span>
          </router-link>
        </li>

        <!-- Right scroll arrow -->
        <li
          class="right-arrow"
          :class="{'d-none': layout.settings.layout_type==='rtl'? hideRightArrowRTL : hideRightArrow }"
          role="button"
          tabindex="0"
          aria-label="Scroll right"
          @click="(layout.settings.sidebar.type === 'horizontal_sidebar' && layout.settings.layout_type==='rtl') ? scrollToRightRTL() : scrollToRight()"
        >
          <i
            class="fa fa-angle-right"
            aria-hidden="true"
          />
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
/**
 * Sidebar Component
 * 
 * A modern, accessible sidebar navigation component that provides the main
 * navigation structure for the application. Features include:
 * 
 * - Responsive design that works on all device sizes
 * - Accessibility support with ARIA attributes
 * - SVG icons for crisp display at any resolution
 * - Smooth transitions and animations
 * - User/company profile display
 * - RTL support
 * - Horizontal scrolling for overflow menu items
 */
import {mapState} from "vuex";
import {mediaBase} from "@/repositories/Repository";
import {getCompanyFormat} from "@/repositories/Utils";
import ProfileTypeBadge from "@/components/ProfileTypeBadge.vue";

export default {
  name: "Sidebar",
  components: {ProfileTypeBadge},
  data() {
    return {
      width: 0,
      height: 0,
      margin: 0,
      hideRightArrowRTL: true,
      hideLeftArrowRTL: true,
      hideRightArrow: true,
      hideLeftArrow: true,
      menuWidth: 0
    };
  },
  computed: {
    ...mapState({
      menuItems: state => state.menu.data,
      layout: state => state.layout.layout,
      sidebar: state => state.layout.sidebarType,
      user: state => state.auth.user,
      company: state => state.auth.company,
      sessionType: state => state.auth.sessionType,
      profile: state => state.auth.profile,
      position: state => state.auth.position,
    }),
  },
  created() {
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
    if (this.width < 991) {
      this.layout.settings.sidebar.type = "default";
    }
    const val = this.sidebar
    if (val === 'default') {
      this.layout.settings.sidebar.type = 'default';
      this.layout.settings.sidebar.body_type = 'default';
    } else if (val === 'compact') {
      this.layout.settings.sidebar.type = 'compact-wrapper';
      this.layout.settings.sidebar.body_type = 'sidebar-icon';
    } else if (val === 'compact-icon') {
      this.layout.settings.sidebar.type = 'compact-page';
      this.layout.settings.sidebar.body_type = 'sidebar-hover';
    } else if (val === 'horizontal') {
      this.layout.settings.sidebar.type = 'horizontal_sidebar';
      this.layout.settings.sidebar.body_type = '';
    }
    setTimeout(() => {
      const elmnt = document.getElementById("myDIV");
      this.menuWidth = elmnt.offsetWidth;
      this.menuWidth > window.innerWidth ? (this.hideRightArrow = false, this.hideLeftArrowRTL = false) : (this.hideRightArrow = true, this.hideLeftArrowRTL = true)
    }, 500)
  },
  destroyed() {
    window.removeEventListener("resize", this.handleResize);
  },
  mounted() {
    this.menuItems.filter(items => {
      if (items.path === this.$route.path)
        this.$store.dispatch("menu/setActiveRoute", items);
      if (!items.children) return false;
      items.children.filter(subItems => {
        if (subItems.path === this.$route.path)
          this.$store.dispatch("menu/setActiveRoute", subItems);
        if (!subItems.children) return false;
        subItems.children.filter(subSubItems => {
          if (subSubItems.path === this.$route.path)
            this.$store.dispatch("menu/setActiveRoute", subSubItems);
        });
      });
    });
  },
  methods: {

    getImgUrl() {
      if (this.sessionType === 'company') {
        return (this.company && this.company.logo ? mediaBase + '/thumbnailCropped' + this.company.logo : require("../assets/images/dashboard/user.png"))
      } else {
        return (this.user && this.user.photo ? mediaBase + '/thumbnailCropped' + this.user.photo : require("../assets/images/dashboard/user.png"))
      }
    },
    setNavActive(item) {
      this.$store.dispatch("menu/setNavActive", item);
    },
    handleResize() {
      this.width = window.innerWidth - 310;
    },
    scrollToRightRTL() {
      this.temp = this.width + this.margin;
      // Checking condition for remaing margin
      if (this.temp === 0) {
        this.margin = this.temp;
        this.hideRightArrowRTL = true;
      }
      // else
      else {
        this.margin += this.width;
        this.hideRightArrowRTL = false;
        this.hideLeftArrowRTL = false;
      }
    },
    scrollToLeftRTL() {
      // If Margin is reach between screen resolution
      if (this.margin <= -this.width) {
        this.margin += -this.width;
        this.hideLeftArrowRTL = true;
      }
      //Else
      else {
        this.margin += -this.width;
        this.hideRightArrowRTL = false;
      }
    },
    scrollToLeft() {

      // If Margin is reach between screen resolution
      if (this.margin >= -this.width) {
        this.margin = 0;
        this.hideLeftArrow = true;
      }
      //Else
      else {
        this.margin += this.width;
        this.hideRightArrow = false;
      }
    },
    scrollToRight() {
      this.temp = this.menuWidth + this.margin;
      // Checking condition for remaing margin
      if (this.temp < this.menuWidth) {
        this.margin = -this.temp;
        this.hideRightArrow = true;
      }
      // else
      else {
        this.margin += -this.width;
        this.hideLeftArrow = false;
      }
    },
    getCompanyFormat(keyword) {
      return getCompanyFormat(keyword, this.$i18n.locale);
    },

    openFeedbackForm() {
      this.$bvModal.show('modal-feedback-form')
    }
  }
};
</script>

<style lang="scss">
/* ===== Sidebar Component Styles ===== */

/* Sidebar header styles */
.menu-group-header {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 10px;
  margin-left: 20px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Logo styles */
.logo-image {
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
}

/* User profile section styles */
.sidebar-user {
  padding: 0 20px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 20px;

  .user-avatar-container {
    position: relative;
    display: inline-block;
  }

  .user-avatar {
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: scale(1.05);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    }
  }

  .verification-badge {
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(30%, -30%);
  }

  .verification-icon {
    width: 30px;
    height: 30px;
    background-color: white;
    border-radius: 50%;
    padding: 3px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);

    &.verified {
      border: 2px solid #28a745;
    }

    &.unverified {
      border: 2px solid #dc3545;
    }
  }

  .user-name, .company-name {
    font-weight: 600;
    color: white;
    margin-bottom: 5px;

  }

  .company-name {
    text-align: center;
    margin: 4px;
    padding: 20px;
  }

  .user-id {
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
  }
}

/* Menu item styles */
.menu-item {
  margin: 2px 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .menu-link {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: rgba(255, 255, 255, 1) !important;
    padding: 12px 20px;
    line-height: 24px;
    border-radius: 6px;
    transition: all 0.3s ease;

    .menu-icon {
      height: 20px;
      width: 20px;
      margin-right: 20px;
      stroke: rgba(255, 255, 255, 0.9);
      transition: transform 0.3s ease, stroke 0.3s ease;
    }

    span {
      transition: transform 0.3s ease;
    }

    &:hover, &.router-link-active {
      background: rgba(255, 255, 255, 0.1);
      transform: translateX(5px);

      .menu-icon {
        stroke: white;
        transform: scale(1.1);
      }
    }

    &.router-link-active {
      font-weight: 600;
      background: rgba(255, 255, 255, 0.15);
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    }
  }
}

/* Scroll arrow styles */
.left-arrow, .right-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  i {
    color: white;
  }
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .sidebar {
    width: 100%;
    max-width: 280px;
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 999;
    transform: translateX(-100%);
    transition: transform 0.3s ease;

    &.show {
      transform: translateX(0);
    }
  }

  .menu-item .menu-link {
    padding: 10px 15px;

    .menu-icon {
      margin-right: 15px;
    }
  }
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .menu-item .menu-link:hover, 
  .menu-item .menu-link.router-link-active {
    background: rgba(255, 255, 255, 0.15);
  }
}
</style>
